import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, InfoNotice, AppButton, FlexibleSelectDropdown, DropdownSort,
} from '@Components/Control';
import { ProductFormCheckBoxInput } from '@Components/Partial/Product/NewSearch';
import { AdminNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  initiateFetchAdminOnboardings,
  clearAdminOnboardings,
} from '@Actions';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
  SearchInput,
} from '../../../Partial/Product/SharedComponents';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { api: 'prefilled', display: 'Onboarding Pending' },
  { api: 'submitted', display: 'Onboarding Completed' },
  { api: 'payment_pending', display: 'Payment Pending' },
  { api: 'payment_success', display: 'Payment Completed' },
];

/* eslint-disable camelcase */
const STATUS_LABELS = {
  prefilled: 'Onboarding Pending',
  submitted: 'Onboarding Completed',
  payment_pending: 'Payment Pending',
  payment_success: 'Payment Completed',
};
/* eslint-enable camelcase */

/* eslint-disable camelcase */
const INVOICE_STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  void: 'Void',
  payment_due: 'Payment Due',
};
/* eslint-enable camelcase */

const SORT_OPTIONS = [
  { api: 'created_at', display: 'Created at' },
  { api: 'status', display: 'Status' },
  { api: 'contact.email', display: 'Email' },
];

const HEADER = [
  { display: 'Customer', uid: 'customer', size: '16%' },
  { display: 'Email', uid: 'email', size: '15%' },
  { display: 'Account Type', uid: 'accountType', size: '9%' },
  { display: 'Prefilled', uid: 'prefilled', size: '8%' },
  { display: 'Status', uid: 'status', size: '13%' },
  { display: 'Payment', uid: 'payment', size: '10%' },
  { display: 'Created', uid: 'createdAt', size: '10%' },
  { display: '', uid: 'actions', size: '19%' },
];

/* eslint-disable camelcase */
const STATUS_COLORS = {
  prefilled: { bg: '#8b5cf622', border: '#8b5cf6', text: '#8b5cf6' },
  submitted: { bg: '#3b82f622', border: '#3b82f6', text: '#3b82f6' },
  payment_pending: { bg: '#f59e0b22', border: '#f59e0b', text: '#f59e0b' },
  payment_success: { bg: '#00a85522', border: '#00a855', text: '#00a855' },
};

const PAYMENT_COLORS = {
  paid: { bg: '#00a85522', border: '#00a855', text: '#00a855' },
  payment_due: { bg: '#e53e3e22', border: '#e53e3e', text: '#e53e3e' },
  draft: { bg: '#71717a22', border: '#71717a', text: '#71717a' },
  sent: { bg: '#3b82f622', border: '#3b82f6', text: '#3b82f6' },
  void: { bg: '#71717a22', border: '#71717a', text: '#71717a' },
};
/* eslint-enable camelcase */

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const AdminOnboardingsListPage = ({
  small,
  location,
  loading,
  onboardingsData = null,
  fetchOnboardings,
  clearOnboardings,
}) => {
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const [copiedId, setCopiedId] = React.useState(null);

  const handleCopyLink = (row) => {
    const url = row.prefill?.url;
    if (!url) return;
    // eslint-disable-next-line no-underscore-dangle
    navigator.clipboard.writeText(url).then(() => {
      // eslint-disable-next-line no-underscore-dangle
      setCopiedId(row.id || row._id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };
  const usePageBg = isDarkMode ? page.background : page.altBg;

  const [currentPage, setCurrentPage] = React.useState(0);
  const [query, setQuery] = React.useState({});
  const [searchInput, setSearchInput] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [isPrefilled, setIsPrefilled] = React.useState(false);
  const debouncedSearch = useDebouncedValue(searchInput, 400);

  const queryParams = React.useMemo(() => {
    const p = {
      limit: PAGE_SIZE,
      skip: currentPage * PAGE_SIZE,
    };
    if (query.status) p.status = query.status;
    if (query.sortField) p.sortField = query.sortField;
    if (query.sortOrder) p.sortOrder = query.sortOrder;
    if (debouncedSearch) p.search = debouncedSearch;
    /* eslint-disable camelcase */
    if (isPrefilled) p.is_prefilled = 'true';
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    /* eslint-enable camelcase */
    return p;
  }, [currentPage, query, debouncedSearch, isPrefilled, dateFrom, dateTo]);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [query, debouncedSearch, isPrefilled, dateFrom, dateTo]);

  React.useEffect(() => {
    fetchOnboardings(queryParams);
    return () => {
      clearOnboardings();
    };
  }, [queryParams, fetchOnboardings]);

  const handleQueryChange = (key, value) => {
    if (key === 'reset') {
      setQuery({});
      setSearchInput('');
      setDateFrom('');
      setDateTo('');
      setIsPrefilled(false);
      setCurrentPage(0);
    } else {
      setQuery((prev) => {
        const next = { ...prev, [key]: value };
        if (!value) delete next[key];
        return next;
      });
    }
  };

  const handleSortFieldChange = (field, direction) => {
    handleQueryChange('sortField', field);
    handleQueryChange('sortOrder', direction);
  };

  const handleSortDirectionToggle = () => {
    handleQueryChange('sortOrder', query.sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const { data = [], pagination = {} } = onboardingsData || {};
  const { totalResults = 0, skip = 0, limit = PAGE_SIZE } = pagination;

  const renderStatusBadge = (value, colorMap, labelMap = null) => {
    const colors = colorMap[value] || { bg: '#71717a22', border: '#71717a', text: '#71717a' };
    let label;
    if (labelMap) {
      label = labelMap[value] || value || '—';
    } else {
      label = value ? String(value).replace(/_/g, ' ') : '—';
    }
    return (
      <Box
        background={colors.bg}
        border={{ color: colors.border, size: '1px' }}
        round="6px"
        pad={{ vertical: '0.2rem', horizontal: '0.5rem' }}
        width="fit-content"
      >
        <Text size="0.8rem" weight={600} color={colors.text}>
          {label}
        </Text>
      </Box>
    );
  };

  const renderCell = (uid, row) => {
    if (uid === 'customer') {
      return (
        <Box direction="column" gap="0.1rem">
          <Text size="0.95rem" color={page.tableText} weight={500}>
            {row.contact?.companyName || row.contact?.fullName || '—'}
          </Text>
          {row.contact?.companyName && (
            <Text size="0.8rem" color={page.secondaryText}>
              {row.contact.fullName}
            </Text>
          )}
        </Box>
      );
    }

    if (uid === 'email') {
      return (
        <Text size="0.9rem" color={page.tableText}>
          {row.contact?.email || '—'}
        </Text>
      );
    }

    if (uid === 'accountType') {
      return (
        <Text size="0.9rem" color={page.tableText} style={{ textTransform: 'capitalize' }}>
          {row.accountType || '—'}
        </Text>
      );
    }

    if (uid === 'prefilled') {
      const isPre = Boolean(row.prefill?.tokenHash || row.prefill?.token_hash);
      return (
        <Text size="0.9rem" color={isPre ? page.primaryText : page.secondaryText}>
          {isPre ? 'Yes' : 'No'}
        </Text>
      );
    }

    if (uid === 'status') {
      return renderStatusBadge(row.status, STATUS_COLORS, STATUS_LABELS);
    }

    if (uid === 'payment') {
      const invoiceStatus = row.billing?.ghlInitialInvoiceStatus;
      if (!invoiceStatus) {
        return <Text size="0.9rem" color={page.secondaryText}>—</Text>;
      }
      return renderStatusBadge(invoiceStatus, PAYMENT_COLORS, INVOICE_STATUS_LABELS);
    }

    if (uid === 'referralToken') {
      return (
        <Text size="0.8rem" color={page.secondaryText} style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
          {row.referralToken || '—'}
        </Text>
      );
    }

    if (uid === 'createdAt') {
      return (
        <Text size="0.85rem" color={page.tableText}>
          {buildDateTimeLabel(row.createdAt)}
        </Text>
      );
    }

    if (uid === 'actions') {
      // eslint-disable-next-line no-underscore-dangle
      const rowId = row.id || row._id;
      const prefillUrl = row.prefill?.url;
      const copied = copiedId === rowId;
      return (
        <Box direction="row" gap="0.4rem" style={{ whiteSpace: 'nowrap' }} align="center">
          {prefillUrl && (
            <AppButton
              overrideHover
              level="tableControls"
              label={copied ? 'Copied!' : 'Copy Link'}
              bgColor={copied ? '#00a855' : button.primaryBg}
              color={button.primaryText}
              onClick={() => handleCopyLink(row)}
            />
          )}
          <AppButton
            overrideHover
            level="tableControls"
            label="Details"
            bgColor={button.primaryBg}
            color={button.primaryText}
            onClick={() => navigate(paths.adminOnboardingDetails.replace(':id', rowId))}
          />
        </Box>
      );
    }

    return <Text size="0.9rem" color={page.tableText}>{row[uid] || '—'}</Text>;
  };

  const querySelected = query.status || query.sortField || query.sortOrder
    || searchInput || isPrefilled || dateFrom || dateTo;

  const renderContents = () => {
    if (loading || data?.length === 0) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={data?.length === 0 && Boolean(querySelected)}
          refreshQuery={() => handleQueryChange('reset')}
          buttonHighlight={button.primaryBg}
          loading={loading}
          label="Loading onboardings..."
          noDataLabel="No onboardings found. Try adjusting your filters."
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            wrap={false}
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box key={uid} width={size} pad={{ horizontal: '0.5rem', top: '1rem', bottom: '0.5rem' }}>
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {data.map((row) => (
          <Box
            // eslint-disable-next-line no-underscore-dangle
            key={row.id || row._id}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
            pad={{ vertical: '1rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={small ? '100%' : size}
                pad={{ horizontal: small ? '0' : '0.5rem', vertical: small ? '0.25rem' : '0' }}
                direction="row"
                gap="0.5rem"
              >
                {small && (
                  <Text size="0.875rem" color={page.tableHeader}>
                    {display}
                    :
                  </Text>
                )}
                {renderCell(uid, row)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  const tableControls = (
    <Box wrap direction="row" gap="0.5rem" align="center">
      <SearchInput
        key="search"
        customPlaceholder="Search by name, email, token"
        small={small}
        handleSearch={(_, value) => { setSearchInput(value); }}
        initialValue={searchInput}
      />
      <Box>
        <FlexibleSelectDropdown
          textColor={page.inputText}
          handleItemSelect={(value) => handleQueryChange('status', value || undefined)}
          flexibleOptions={STATUS_OPTIONS}
          noSelectionLabel="All statuses"
          displayKey="display"
          valueKey="api"
          iconPath="images/filter-input-icon.svg"
          customPad="0.2rem 0.3rem"
          selectedOption={
            STATUS_OPTIONS.find((o) => o.api === (query.status || ''))?.display
            || 'All statuses'
          }
          withReset={!!query.status}
        />
      </Box>
      <Box>
        <DropdownSort
          sortOptions={SORT_OPTIONS}
          selectedSortField={query.sortField}
          sortDirection={query.sortOrder || 'desc'}
          onSortFieldChange={handleSortFieldChange}
          onSortDirectionToggle={handleSortDirectionToggle}
          textColor={page.inputText}
          iconColor={page.inputIcon}
          buttonBg={page.inputBg}
          customPad="0.2rem 0.3rem"
        />
      </Box>
      <Box direction="row" gap="0.35rem" align="center">
        <Text size="0.82rem" color={page.secondaryText} style={{ whiteSpace: 'nowrap' }}>From:</Text>
        <input
          type="date"
          value={dateFrom}
          max={dateTo || undefined}
          onChange={(e) => setDateFrom(e.target.value)}
          style={{
            padding: '0.2rem 0.4rem',
            fontSize: '0.82rem',
            border: `1px solid ${page.inputBorder || '#ccc'}`,
            borderRadius: 'var(--app-radius)',
            background: page.inputBg,
            color: page.inputText,
            outline: 'none',
            cursor: 'pointer',
          }}
        />
      </Box>
      <Box direction="row" gap="0.35rem" align="center">
        <Text size="0.82rem" color={page.secondaryText} style={{ whiteSpace: 'nowrap' }}>To:</Text>
        <input
          type="date"
          value={dateTo}
          min={dateFrom || undefined}
          onChange={(e) => setDateTo(e.target.value)}
          style={{
            padding: '0.2rem 0.4rem',
            fontSize: '0.82rem',
            border: `1px solid ${page.inputBorder || '#ccc'}`,
            borderRadius: 'var(--app-radius)',
            background: page.inputBg,
            color: page.inputText,
            outline: 'none',
            cursor: 'pointer',
          }}
        />
      </Box>
      <Box direction="row" align="center" pad={{ horizontal: '0.25rem' }}>
        <ProductFormCheckBoxInput
          controlled
          value={isPrefilled}
          handleFormValues={(val) => setIsPrefilled(val)}
          label="Prefilled only"
          labelColor={page.primaryText}
          labelSize="0.85rem"
          checkmarkColor={page.highlight}
          borderColor={page.inputBorder}
          focusHighlight={page.focusHighlight}
          customBoxSize="1rem"
          customCheckmarkSize="0.65rem"
        />
      </Box>
      <AppButton
        overrideHover
        disabled={!querySelected}
        onClick={() => handleQueryChange('reset')}
        level="tableControls"
        height="var(--table-controls-height)"
        color={button.primaryText}
        bgColor={button.primaryBg}
        label="Reset"
      />
    </Box>
  );

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Onboarding Tracker',
        subTitle: 'Onboarding · Admin',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
        width="100%"
      >
        <InfoNotice
          header="Customer onboardings"
          text="Track all customer onboarding submissions. Filter by status, search by name or email, and view full details for each entry."
        />
        <Box width="100%">
          <TableWrapper
            zeroBasedPageIndex
            small={small}
            header="Onboardings"
            boxPad={tablePadding(small)}
            isPaged={totalResults > limit}
            totalResults={totalResults}
            currentResults={data.length}
            selectedResultsCount={limit}
            currentPage={currentPage}
            handlePage={(p) => setCurrentPage(p)}
            overrideNextPageDisabled={skip + limit >= totalResults}
            tableControls={tableControls}
          >
            <Box fill="horizontal" direction="column">
              {renderContents()}
            </Box>
          </TableWrapper>
        </Box>
      </Box>
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    onboardingsData: state.adminOnboardings.onboardings,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchOnboardings: initiateFetchAdminOnboardings,
      clearOnboardings: clearAdminOnboardings,
    },
    dispatch,
  );
}

AdminOnboardingsListPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  fetchOnboardings: PropTypes.func.isRequired,
  clearOnboardings: PropTypes.func.isRequired,
  onboardingsData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({})),
    pagination: PropTypes.shape({
      totalResults: PropTypes.number,
      skip: PropTypes.number,
      limit: PropTypes.number,
    }),
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminOnboardingsListPage);
